/**
 * Network Store
 *
 * Manages network state, DAG metrics, and real-time updates.
 * Real-time updates via WebSocket events through LiveUpdateManager.
 *
 * MIGRATED TO USE GENERATED OPENAPI TYPES
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/api/client'
import { typedApi } from '@/api/typed-client'
import { useStreamingStore } from '@/stores/streaming'
import { useWebSocketStatus } from '@/services/LiveUpdateManager'
import type { components } from '@/types/api'
import type { DAGNode } from '@/types/visualization'

// Use auto-generated types from OpenAPI spec
type Message = components['schemas']['MessageSummary']
type TransactionEntry = components['schemas']['TransactionEntry']
type SupplyResponse = components['schemas']['SupplyResponse']
type TreasuryResponse = components['schemas']['TreasuryResponse']
type DiversityMetrics = components['schemas']['DiversityMetrics']
type AdmissibilityOverviewData = components['schemas']['AdmissibilityOverviewData']

export const useNetworkStore = defineStore('network', () => {
  // Get WebSocket status from LiveUpdateManager (single source of truth)
  const { connectionStatus, isConnected: wsConnected } = useWebSocketStatus()

  // State
  const networkHeight = ref(0)  // Deprecated, use dagDepth
  const dagDepth = ref(0)
  const finalizedHeight = ref(0)
  const tps = ref(0)
  const totalMessages = ref(0)
  const finalizedCount = ref(0)
  const recentMessages = ref<Message[]>([])  // Now using auto-generated MessageSummary type
  const recentDAGNodes = ref<DAGNode[]>([])
  const tips = ref<string[]>([])

  // New consensus metrics state
  const diversityScore = ref(0)
  const activeConflicts = ref(0)
  const admissibilityRate = ref(0)
  const securityScore = ref(0)

  // Streaming store reference
  const streamingStore = useStreamingStore()

  // Computed
  const isConnected = wsConnected  // Use connection status from LiveUpdateManager

  // Streaming status computed properties
  const streamingMode = computed(() => streamingStore.status.mode)
  const isRealTimeStreaming = computed(() => streamingStore.isRealtime)
  const streamingHealth = computed(() => streamingStore.status.healthy)

  // Actions
  const initialize = async () => {
    console.log('[network.ts] Initializing network store...')

    // Setup event listeners for LiveUpdateManager events
    setupEventListeners()

    // Load initial data
    await loadNetworkStats()
    await loadRecentMessages()
    console.log('[network.ts] Network stats loaded:', {
      networkHeight: networkHeight.value,
      totalMessages: totalMessages.value,
      tps: tps.value,
      finalizedCount: finalizedCount.value,
      recentMessages: recentMessages.value.length
    })
  }

  // Event listener handlers (receive events from LiveUpdateManager)
  const handleNewMessageEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 message.new event:', data)
    handleNewMessage(data)
  }

  const handleTipsUpdateEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 tips.update event:', data)
    handleTipsUpdate(data)
  }

  const handleKCoreFinalityEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 finality.kcore event:', data)
    handleKCoreFinality(data)
  }

  const handleHomologyFinalityEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 finality.homology event:', data)
    handleHomologyFinality(data)
  }

  const handleMetricsUpdateEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 metrics.update event:', data)
    handleMetricsUpdate(data)
  }

  const handleTransactionEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 transaction/transfer event:', data)
    handleNewTransaction(data)
  }

  const handleReputationUpdateEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 reputation.update event:', data)
    handleReputationUpdate(data)
  }

  const handleDiversityUpdateEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 diversity.update event:', data)
    handleDiversityUpdate(data)
  }

  const handleEnergyUpdateEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 energy.update event:', data)
    handleEnergyUpdate(data)
  }

  const handleKCoreUpdateEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 kcore.update event:', data)
    handleKCoreUpdate(data)
  }

  const handleAdmissibilityUpdateEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 admissibility.update event:', data)
    handleAdmissibilityUpdate(data)
  }

  const handleEconomicsUpdateEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 economics.update event:', data)
    handleEconomicsUpdate(data)
  }

  const handleTreasuryUpdateEvent = (event: Event) => {
    const data = (event as CustomEvent).detail
    console.log('[network.ts] 📨 treasury.update event:', data)
    handleTreasuryUpdate(data)
  }

  /**
   * Setup event listeners for LiveUpdateManager events
   * This replaces the old WebSocket connection logic
   */
  const setupEventListeners = () => {
    console.log('[network.ts] 📡 Setting up event listeners for LiveUpdateManager events')

    // Listen to events dispatched by LiveUpdateManager
    window.addEventListener('dag.message.new', handleNewMessageEvent)
    window.addEventListener('message.new', handleNewMessageEvent)
    window.addEventListener('tips.update', handleTipsUpdateEvent)
    window.addEventListener('finality.kcore', handleKCoreFinalityEvent)
    window.addEventListener('finality.homology', handleHomologyFinalityEvent)
    window.addEventListener('metrics.update', handleMetricsUpdateEvent)
    window.addEventListener('transaction.new', handleTransactionEvent)
    window.addEventListener('transfer.recorded', handleTransactionEvent)
    window.addEventListener('reputation.update', handleReputationUpdateEvent)
    window.addEventListener('diversity.update', handleDiversityUpdateEvent)
    window.addEventListener('energy.update', handleEnergyUpdateEvent)
    window.addEventListener('kcore.update', handleKCoreUpdateEvent)
    window.addEventListener('admissibility.update', handleAdmissibilityUpdateEvent)
    window.addEventListener('economics.update', handleEconomicsUpdateEvent)
    window.addEventListener('treasury.update', handleTreasuryUpdateEvent)

    console.log('[network.ts] ✅ Event listeners registered')
  }

  /**
   * Cleanup event listeners on store disposal
   */
  const cleanup = () => {
    console.log('[network.ts] 🧹 Cleaning up event listeners')

    window.removeEventListener('dag.message.new', handleNewMessageEvent)
    window.removeEventListener('message.new', handleNewMessageEvent)
    window.removeEventListener('tips.update', handleTipsUpdateEvent)
    window.removeEventListener('finality.kcore', handleKCoreFinalityEvent)
    window.removeEventListener('finality.homology', handleHomologyFinalityEvent)
    window.removeEventListener('metrics.update', handleMetricsUpdateEvent)
    window.removeEventListener('transaction.new', handleTransactionEvent)
    window.removeEventListener('transfer.recorded', handleTransactionEvent)
    window.removeEventListener('reputation.update', handleReputationUpdateEvent)
    window.removeEventListener('diversity.update', handleDiversityUpdateEvent)
    window.removeEventListener('energy.update', handleEnergyUpdateEvent)
    window.removeEventListener('kcore.update', handleKCoreUpdateEvent)
    window.removeEventListener('admissibility.update', handleAdmissibilityUpdateEvent)
    window.removeEventListener('economics.update', handleEconomicsUpdateEvent)
    window.removeEventListener('treasury.update', handleTreasuryUpdateEvent)

    console.log('[network.ts] ✅ Event listeners removed')
  }
  
  const loadNetworkStats = async () => {
    try {
      console.log('Loading network stats from API...')
      const data = await typedApi.getAnalyticsOverview()
      console.log('API response:', data)

      dagDepth.value = data.dag_depth
      networkHeight.value = data.network_height  // Keep for backward compatibility
      finalizedHeight.value = data.finalized_height
      tps.value = data.tps
      totalMessages.value = data.total_messages
      finalizedCount.value = data.finalized_count

      // Load consensus metrics on startup
      try {
        const consensusResponse = await apiClient.get('/consensus/overview')
        const consensusData = consensusResponse.data

        diversityScore.value = consensusData.diversity?.score || 0
        activeConflicts.value = consensusData.energy?.active_conflicts || 0
        admissibilityRate.value = consensusData.admissibility?.overall_rate || 0

        // Calculate security score
        const securityResponse = await apiClient.get('/consensus/security')
        securityScore.value = securityResponse.data.overall_score || 0
      } catch (err) {
        console.log('Consensus metrics not available yet')
      }
    } catch (error) {
      console.error('Failed to load network stats:', error)
    }
  }
  
  const loadRecentMessages = async () => {
    try {
      console.log('Loading recent messages from API...')
      const messagesResponse = await typedApi.getMessages({ limit: 10, include_children: true })
      console.log('Messages response:', messagesResponse)

      recentMessages.value = messagesResponse.messages

      // Also load DAG nodes for visualization
      recentDAGNodes.value = await typedApi.getRecentMessagesForVisualization(50)
    } catch (error) {
      console.error('Failed to load recent messages:', error)
    }
  }
  
  const handleNewMessage = (message: Partial<Message> & { depth?: number }) => {
    totalMessages.value++
    recentMessages.value.unshift(message as Message)

    // Keep only last 100 messages
    if (recentMessages.value.length > 100) {
      recentMessages.value.pop()
    }

    // Update network height if needed
    if (message.depth && message.depth > networkHeight.value) {
      networkHeight.value = message.depth
    }
  }
  
  const handleKCoreFinality = (data: { message_id?: string; k_core_value?: number }) => {
    finalizedCount.value++

    // Update message in recent messages if present
    const index = recentMessages.value.findIndex(m => m.id === data.message_id)
    if (index !== -1) {
      recentMessages.value[index].k_core_finalized = true
      recentMessages.value[index].k_core_value = data.k_core_value
    }
  }

  const handleHomologyFinality = (data: { message_id?: string; homology_betti_b1?: number }) => {
    // Update message in recent messages if present
    const index = recentMessages.value.findIndex(m => m.id === data.message_id)
    if (index !== -1) {
      recentMessages.value[index].homology_finalized = true
      recentMessages.value[index].homology_betti_b1 = data.homology_betti_b1
    }
  }
  
  const handleMetricsUpdate = (metrics: {
    tps?: number
    dag_depth?: number
    network_height?: number
    finalized_height?: number
    total_messages?: number
    finalized_count?: number
  }) => {
    if (metrics.tps !== undefined) tps.value = metrics.tps
    if (metrics.dag_depth !== undefined) dagDepth.value = metrics.dag_depth
    if (metrics.network_height !== undefined) networkHeight.value = metrics.network_height
    if (metrics.finalized_height !== undefined) finalizedHeight.value = metrics.finalized_height
    if (metrics.total_messages !== undefined) totalMessages.value = metrics.total_messages
    if (metrics.finalized_count !== undefined) finalizedCount.value = metrics.finalized_count
  }

  const handleTipsUpdate = (data: { tips?: string[] }) => {
    tips.value = data.tips || []
  }

  const handleNewTransaction = (transaction: Partial<TransactionEntry>) => {
    // Emit event that wallet store can listen to
    window.dispatchEvent(new CustomEvent('new-transaction', { detail: transaction }))
  }

  const handleReputationUpdate = (reputation: { address?: string; score?: number; [key: string]: any }) => {
    // Emit event that dashboard can listen to for validator updates
    window.dispatchEvent(new CustomEvent('reputation-update', { detail: reputation }))
  }

  const handleDiversityUpdate = (data: Partial<DiversityMetrics> & { diversity_score?: number }) => {
    diversityScore.value = data.diversity_score || data.score || 0
    // Emit event for components that need diversity data
    window.dispatchEvent(new CustomEvent('diversity-update', { detail: data }))
  }

  const handleEnergyUpdate = (data: { active_conflicts?: number; [key: string]: any }) => {
    activeConflicts.value = data.active_conflicts || 0
    // Emit event for components that need energy data
    window.dispatchEvent(new CustomEvent('energy-update', { detail: data }))
  }

  const handleKCoreUpdate = (data: { finalized_count?: number; [key: string]: any }) => {
    // Update finalized count if provided
    if (data.finalized_count !== undefined) {
      finalizedCount.value = data.finalized_count
    }
    // Emit event for components that need k-core data
    window.dispatchEvent(new CustomEvent('kcore-update', { detail: data }))
  }

  const handleAdmissibilityUpdate = (data: Partial<AdmissibilityOverviewData> & { rates?: { overall?: number } }) => {
    if (data.rates && data.rates.overall !== undefined) {
      admissibilityRate.value = data.rates.overall
    }
    // Emit event for components that need admissibility data
    window.dispatchEvent(new CustomEvent('admissibility-update', { detail: data }))
  }

  const handleEconomicsUpdate = (data: Partial<SupplyResponse>) => {
    // Update economics metrics
    if (data.total_supply !== undefined || data.circulating_supply !== undefined) {
      // Emit event for components that need economics data
      window.dispatchEvent(new CustomEvent('economics-update', { detail: data }))
    }
  }

  const handleTreasuryUpdate = (data: Partial<TreasuryResponse> & { new_proposal?: any }) => {
    // Update treasury data
    window.dispatchEvent(new CustomEvent('treasury-update', { detail: data }))

    // Special handling for new proposals
    if (data.new_proposal) {
      // Could show a notification or update a proposal counter
      window.dispatchEvent(new CustomEvent('treasury-proposal-new', { detail: data.new_proposal }))
    }
  }

  return {
    // State
    connectionStatus,
    networkHeight,
    dagDepth,
    finalizedHeight,
    tps,
    totalMessages,
    finalizedCount,
    recentMessages,
    tips,
    diversityScore,
    activeConflicts,
    admissibilityRate,
    securityScore,

    // Computed
    isConnected,
    streamingMode,
    isRealTimeStreaming,
    streamingHealth,

    // Streaming store access
    streamingStore,

    // Actions
    initialize,
    cleanup,  // Call this when disposing the store
    loadNetworkStats
  }
})